
import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import api from '../api/axios';

export default function ResetPasswordPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!password || !confirmPassword) return;

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await api.post(`/auth/reset-password/${token}`, {
                password,
                confirmPassword,
            });

            await response.data;

            if (response.status === 200) {
                setSuccess(true);
                // Auto redirect to login after 3 seconds
                setTimeout(() => navigate('/login'), 3000);
            } else {
                setError('Failed to reset password');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center"
                style={{ background: "linear-gradient(135deg, #4e73df 0%, #1cc88a 100%)" }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6 col-lg-5">
                            <div className="card shadow-lg border-0 rounded-4">
                                <div className="card-body p-5 text-center">
                                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 bg-success"
                                        style={{ width: "60px", height: "60px" }}>
                                        <CheckCircle size={32} className="text-white" />
                                    </div>
                                    <h2 className="fw-bold text-dark mb-3">Password Reset Successfully!</h2>
                                    <p className="text-muted mb-4">
                                        Your password has been reset successfully. You will be redirected to the login page shortly.
                                    </p>
                                    <Link to="/login" className="btn btn-primary btn-lg rounded-pill">
                                        Continue to Login
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center"
            style={{ background: "linear-gradient(135deg, #4e73df 0%, #1cc88a 100%)" }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-5">
                        <div className="card shadow-lg border-0 rounded-4">
                            <div className="card-body p-5">
                                {/* Header */}
                                <div className="text-center mb-4">
                                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 bg-light"
                                        style={{ width: "60px", height: "60px" }}>
                                        <Lock size={32} className="text-primary" />
                                    </div>
                                    <h2 className="fw-bold text-dark">Reset Password</h2>
                                    <p className="text-muted mb-0">
                                        Enter your new password below
                                    </p>
                                </div>

                                {/* Error */}
                                {error && (
                                    <div className="alert alert-danger mb-4" role="alert">
                                        {error}
                                    </div>
                                )}

                                {/* Form */}
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="password" className="form-label fw-semibold">
                                            New Password
                                        </label>
                                        <div className="input-group input-group-lg">
                                            <span className="input-group-text bg-light">
                                                <Lock size={20} className="text-muted" />
                                            </span>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                id="password"
                                                className="form-control"
                                                placeholder="Enter new password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                disabled={loading}
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label htmlFor="confirmPassword" className="form-label fw-semibold">
                                            Confirm Password
                                        </label>
                                        <div className="input-group input-group-lg">
                                            <span className="input-group-text bg-light">
                                                <Lock size={20} className="text-muted" />
                                            </span>
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                id="confirmPassword"
                                                className="form-control"
                                                placeholder="Confirm new password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                disabled={loading}
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg w-100 mb-3 rounded-pill"
                                        disabled={loading || !password || !confirmPassword}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Resetting...
                                            </>
                                        ) : (
                                            "Reset Password"
                                        )}
                                    </button>

                                    <div className="text-center">
                                        <Link to="/login" className="text-decoration-none">
                                            Back to Login
                                        </Link>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
